import * as React from "react";
import { authService } from "@/utils/auth/service";


type AuthContextValue = {
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshAuth: () => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  const refreshAuth = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const token = await authService.ensureSession();
      setIsAuthenticated(!!token);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = React.useCallback(async () => {
    await authService.logout();
    setIsAuthenticated(false);
  }, []);

  React.useEffect(() => {
    void refreshAuth();
  }, [refreshAuth]);

  const value = React.useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      refreshAuth,
      logout,
    }),
    [isAuthenticated, isLoading, refreshAuth, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}